import { Form, Offcanvas } from "react-bootstrap";
import CustomButton from "../../components/CustomButton/CustomButton";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import ReactQuill from "react-quill-new";
import { DashSquareFill, PlusSquareFill } from "react-bootstrap-icons";
import { toast } from "react-toastify";
import WebService from "../../utility/WebService";

interface AddUsWebinarSeriesProps {
    show: boolean;
    handleClose: () => void;
    data?: any;
}

type FormData = {
    title: string;
    seminarDate: string;
    structure: string;
    link: string;
}

const AddUsWebinarSeries = ({ show, handleClose, data }: AddUsWebinarSeriesProps) => {
    const { register, handleSubmit, formState: { errors }, reset } = useForm<FormData>();
    const [isLoading, setLoading] = useState(false);
    const [logo, setLogo] = useState<File | null>(null);
    const [description, setDescription] = useState('');
    const [overview, setOverview] = useState('');
    const [keyParticipants, setKeyParticipants] = useState('');
    const [topic, setTopic] = useState('');
    const [webinarImage, setWebinarImage] = useState<File | null>(null);
    const [potentialTopic, setPotentialTopic] = useState<any[]>([
        { title: '', description: '' }
    ]);
    const [keyMembers, setKeyMembers] = useState<any[]>([
        {}
    ]);

    useEffect(() => {
        if (data) {
            reset({
                title: data.title,
                seminarDate: data.seminarDate ? new Date(data.seminarDate).toISOString().split('T')[0] : '',
                structure: data.structure,
                link: data.link
            });
            setDescription(data.description || '');
            setOverview(data.overview || '');
            setKeyParticipants(data.keyParticipants || '');
            setTopic(data.topic || '');
            setPotentialTopic(data.potentialTopic && data.potentialTopic.length > 0 ? data.potentialTopic : [{ title: '', description: '' }]);
            setKeyMembers(data.keyMembers && data.keyMembers.length > 0 ? data.keyMembers : [{}]);
        }
    }, [data])

    const onSubmit = (request: FormData) => {
        setLoading(true);
        const formData = new FormData();
        formData.append("title", request.title);
        formData.append("description", description);
        formData.append("overview", overview);
        formData.append("seminarDate", request.seminarDate);
        formData.append("structure", request.structure);
        formData.append("link", request.link);
        formData.append("keyParticipants", keyParticipants);
        formData.append("topic", topic);
        if (logo) formData.append("logo", logo);
        if (webinarImage) formData.append("webinarImage", webinarImage);
        if (potentialTopic.length > 0 && potentialTopic.every(eco => eco.title || eco.description)) {
            formData.append("potentialTopic", JSON.stringify(potentialTopic));
        }
        if (keyMembers.length > 0 && keyMembers.every(mem => mem && Object.keys(mem).length !== 0 && mem !== "")) {
            formData.append("keyMembers", JSON.stringify(keyMembers));
        }
        WebService.fileUploadAPI({
            action: `us-webinar-series/addContent`,
            body: formData
        })
            .then((res: any) => {
                toast.success(res.message);
                setLoading(false);
                handleClose();
                reset();
            })
            .catch((err: any) => {
                setLoading(false);
                toast.error(err.response?.data?.message || "An error occurred");
            });
    }

    const uploadFile = (file: File, index: number, type: string, subIndex?: number) => {
        const formData = new FormData();
        formData.append("file", file);
        WebService.fileUploadAPI({
            action: `upload`,
            body: formData
        })
            .then((res: any) => {
                if (res.data) {
                    if (type === 'GALLERY') {
                        setWebinarImage(res.data?.url);
                    } else if (type === 'KEY_MEMBER') {
                        keyMembers[index] = res.data?.url;
                        setKeyMembers([...keyMembers]);
                    }
                }
            })
            .catch((err: any) => {
                toast.error(err.response?.data?.message || "An error occurred");
            });
    }

    return (
        <>
            <Offcanvas show={show} onHide={() => {
                handleClose();
            }}
                placement="end"
                className="offcanvas-custom">
                <Offcanvas.Header closeButton>
                    <Offcanvas.Title>{data ? 'Edit' : 'Add'}</Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body>
                    <Form>
                        <Form.Group className="mb-2" controlId="banner">
                            <Form.Label className="label">Banner</Form.Label>
                            <Form.Control
                                type="file"
                                accept="image/*"
                                className="custom-image-upload"
                                onChange={(e) => {
                                    const input = e.target as HTMLInputElement;
                                    const file = input.files && input.files[0];
                                    if (file) {
                                        setLogo(file);
                                    }
                                }}
                            />
                        </Form.Group>
                        {(logo || data?.logo) && (
                            <div className="mb-2 event-image">
                                <img
                                    src={
                                        logo
                                            ? URL.createObjectURL(logo)
                                            : data?.logo
                                    }
                                    alt="Event"
                                    style={{ maxWidth: "100%", maxHeight: 200, objectFit: "contain" }}
                                />
                            </div>
                        )}
                        <Form.Group className="mb-2" controlId="title">
                            <Form.Label className="label">Title</Form.Label>
                            <Form.Control type="text" placeholder="Enter title"
                                {...register("title", { required: 'Title is required' })} />
                            {errors.title && <div className="error-message">{errors.title.message}</div>}
                        </Form.Group>
                        <Form.Group className="mb-2" controlId="description">
                            <Form.Label className="label">Description</Form.Label>
                            <ReactQuill theme="snow" value={description} onChange={setDescription} className="editor" />
                        </Form.Group>
                        <Form.Group className="mb-2" controlId="overview">
                            <h5>Last Webinar</h5>
                            <Form.Label className="label">Overview</Form.Label>
                            <ReactQuill theme="snow" value={overview} onChange={setOverview} className="editor" placeholder="Overview" />
                        </Form.Group>
                        <Form.Group className="mb-2" controlId="seminarDate">
                            <Form.Label className="label">Seminar Date</Form.Label>
                            <Form.Control type="date" placeholder="Enter seminar date"
                                {...register("seminarDate", { required: 'Seminar date is required' })} />
                            {errors.seminarDate && <div className="error-message">{errors.seminarDate.message}</div>}
                        </Form.Group>
                        <Form.Group className="mb-2" controlId="structure">
                            <Form.Label className="label">Structure</Form.Label>
                            <Form.Control as="textarea" rows={1} placeholder="Enter structure"
                                {...register("structure", { required: 'Structure is required' })} />
                            {errors.structure && <div className="error-message">{errors.structure.message}</div>}
                        </Form.Group>
                        <Form.Group className="mb-2" controlId="link">
                            <Form.Label className="label">Link</Form.Label>
                            <Form.Control as="textarea" rows={1} placeholder="Enter link"
                                {...register("link", {
                                    required: 'Link is required',
                                    pattern: {
                                        value: /^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/[\w\-._~:/?#[\]@!$&'()*+,;=]*)?$/,
                                        message: 'Enter a valid URL'
                                    }
                                })} />
                            {errors.link && <div className="error-message">{errors.link.message}</div>}
                        </Form.Group>
                        <Form.Group className="mb-2" controlId="keyParticipants">
                            <Form.Label className="label">Key Participants</Form.Label>
                            <ReactQuill theme="snow" value={keyParticipants} onChange={setKeyParticipants} className="editor" />
                        </Form.Group>
                        <Form.Group className="mb-2" controlId="topic">
                            <Form.Label className="label">Topic</Form.Label>
                            <ReactQuill theme="snow" value={topic} onChange={setTopic} className="editor" />
                        </Form.Group>
                        <Form.Group className="mb-2" controlId="webinarImage">
                            <Form.Label className="label">Webinar Image</Form.Label>
                            <Form.Control
                                type="file"
                                accept="image/*"
                                className="custom-image-upload"
                                onChange={(e) => {
                                    const input = e.target as HTMLInputElement;
                                    const file = input.files && input.files[0];
                                    if (file) {
                                        uploadFile(file, 0, 'GALLERY');
                                    }
                                }}
                            />
                        </Form.Group>
                        {(webinarImage || data?.webinarImage) && (
                            <div className="mb-2 event-image">
                                <img
                                    src={
                                        webinarImage
                                            ? webinarImage
                                            : data?.webinarImage
                                    }
                                    alt="Event"
                                    style={{ maxWidth: "100%", maxHeight: 200, objectFit: "contain" }}
                                />
                            </div>
                        )}
                        <Form.Group className="mb-2" controlId="potentialTopic">
                            <Form.Label className="label">Potential Topic</Form.Label>
                            {
                                potentialTopic.map((topic, index) => (
                                    <div
                                        key={index}
                                        draggable
                                        onDragStart={e => {
                                            e.dataTransfer.setData("PotentialTopicIndex", index.toString());
                                        }}
                                        onDragOver={e => e.preventDefault()}
                                        onDrop={e => {
                                            const fromIndex = Number(e.dataTransfer.getData("PotentialTopicIndex"));
                                            if (fromIndex === index) return;
                                            const newPotentialTopics = [...potentialTopic];
                                            const [moved] = newPotentialTopics.splice(fromIndex, 1);
                                            newPotentialTopics.splice(index, 0, moved);
                                            setPotentialTopic(newPotentialTopics);
                                        }}
                                        style={{ cursor: "move" }}
                                    >
                                        <div className="d-flex align-items-start">
                                            <span
                                                style={{
                                                    cursor: "grab",
                                                    marginRight: 8,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    fontSize: 20,
                                                    color: "#888",
                                                    marginTop: 10
                                                }}
                                                title="Drag to reorder"
                                            >
                                                <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                                                    <circle cx="5" cy="6" r="1.5" />
                                                    <circle cx="5" cy="10" r="1.5" />
                                                    <circle cx="5" cy="14" r="1.5" />
                                                    <circle cx="11" cy="6" r="1.5" />
                                                    <circle cx="11" cy="10" r="1.5" />
                                                    <circle cx="11" cy="14" r="1.5" />
                                                </svg>
                                            </span>
                                            <div style={{ flex: 1 }} className="highlight-item">
                                                <div className="d-flex">
                                                    <Form.Control
                                                        type="text"
                                                        placeholder={`Name`}
                                                        value={potentialTopic[index].title}
                                                        onChange={(e) => {
                                                            const newPotentialTopics = [...potentialTopic];
                                                            newPotentialTopics[index].title = e.target.value;
                                                            setPotentialTopic(newPotentialTopics);
                                                        }}
                                                    />
                                                    <div className="d-flex mt-2">
                                                        <PlusSquareFill
                                                            onClick={() => {
                                                                setPotentialTopic([...potentialTopic, { title: '', description: '' }]);
                                                            }}
                                                            className="ms-2 me-2 cursor-pointer"
                                                        />
                                                        {potentialTopic.length > 1 && <DashSquareFill
                                                            onClick={() => {
                                                                const newPotentialTopics = potentialTopic.filter((_, i) => i !== index);
                                                                setPotentialTopic(newPotentialTopics);
                                                            }}
                                                            className="cursor-pointer"
                                                        />}
                                                    </div>
                                                </div>
                                                <ReactQuill theme="snow" value={potentialTopic[index].description} onChange={(value) => {
                                                    const newPotentialTopics = [...potentialTopic];
                                                    newPotentialTopics[index].description = value;
                                                    setPotentialTopic(newPotentialTopics);
                                                }} className="editor" />
                                            </div>
                                        </div>
                                    </div>
                                ))
                            }
                        </Form.Group>
                        <Form.Group className="mb-2" controlId="keyMembers">
                            <Form.Label className="label">Key Members and Partners</Form.Label>
                            {
                                keyMembers.map((member, index) => (
                                    <>
                                        <div className="d-flex mt-2">
                                            <Form.Control
                                                type="file"
                                                accept="image/*"
                                                className="custom-image-upload"
                                                onChange={(e) => {
                                                    const input = e.target as HTMLInputElement;
                                                    const file = input.files && input.files[0];
                                                    if (file) {
                                                        uploadFile(file, index, 'KEY_MEMBER');
                                                    }
                                                }}
                                            />
                                            <div className="d-flex mt-2">
                                                <PlusSquareFill
                                                    onClick={() => {
                                                        setKeyMembers([...keyMembers, {}]);
                                                    }}
                                                    className="ms-2 me-2 cursor-pointer"
                                                />
                                                {keyMembers.length > 1 && <DashSquareFill
                                                    onClick={() => {
                                                        const newMembers = keyMembers.filter((_, i) => i !== index);
                                                        setKeyMembers(newMembers);
                                                    }}
                                                    className="cursor-pointer"
                                                />}
                                            </div>
                                        </div>
                                        {(member && Object.keys(member).length !== 0) && (
                                            <div className="mb-1 event-image mt-1">
                                                <img
                                                    src={
                                                        member
                                                    }
                                                    alt="Event"
                                                    style={{ maxWidth: "100%", maxHeight: 200, objectFit: "contain" }}
                                                />
                                            </div>
                                        )}
                                    </>
                                ))
                            }
                        </Form.Group>
                    </Form>
                </Offcanvas.Body>
                <div className="d-flex justify-content-end p-3">
                    <CustomButton
                        isLoading={isLoading}
                        label="Save"
                        className="w-100"
                        onClick={handleSubmit(onSubmit)}
                    />
                </div>
            </Offcanvas>
        </>
    )
}

export default AddUsWebinarSeries;